/**
 * SparkieMarkdown — minimal, safe Markdown-subset renderer for Sparkie agent
 * bubbles.
 *
 * Agentforce composes replies in Markdown but MIAW delivers plain text, so
 * `**bold**` etc. shows through raw. This renders just the inline subset the
 * agent actually emits — bold, italic, inline code, and [text](http(s) links) —
 * as React elements (never innerHTML, so agent-controlled text stays inert).
 * Newlines/lists need no handling: the message bubble is `white-space: pre-wrap`.
 * Headings (`### Title`) render as a bold line.
 */

import React from 'react';

// One alternation, tried in order: code span, link, bold+italic, bold, italic.
// Bold's inner admits lone asterisks (`\*(?!\*)`) so nested emphasis like
// `**bold *inner* text**` recurses instead of leaking raw markers.
// MUST stay a factory returning the regex LITERAL, for two reasons:
// 1. formatInline recurses into bold/link inner text — a shared /g regex's
//    lastIndex would be clobbered mid-loop; each evaluation of a literal is a
//    fresh object.
// 2. Named capture groups only survive the PRODUCTION build inside a literal:
//    CRA's Babel (older-browser targets) rewrites the literal and emulates
//    `.groups` via a wrapper. Rebuilding via `new RegExp(literal.source)`
//    loses the wrapper — matches come back with `groups === undefined` and
//    the renderer throws only in deployed builds (dev/jest keep native named
//    groups). That exact crash shipped to UAT; don't reintroduce it.
const inlineRe = () => /(?<code>`(?<codeText>[^`\n]+)`)|(?<link>\[(?<linkText>[^\]\n]+)\]\((?<href>https?:\/\/[^\s)]+)\))|(?<boldItalic>\*\*\*(?<boldItalicText>[^*\n]+?)\*\*\*)|(?<bold>\*\*(?<boldText>(?:[^*\n]|\*(?!\*))+?)\*\*)|(?<italic>\*(?<italicText>[^*\s](?:[^*\n]*?[^*\s])?)\*)/gi;

const HEADING_RE = /^#{1,6}\s+/;

// Inline formatting for a single line. `allowLinks` is off when recursing into
// link text so links can't nest.
function formatInline(line, keyPrefix, allowLinks = true) {
	const re = inlineRe();
	const nodes = [];
	let last = 0;
	let i = 0;
	let match;
	while ((match = re.exec(line)) !== null) {
		if (match.index > last) {
			nodes.push(line.slice(last, match.index));
		}
		const key = `${keyPrefix}-${i++}`;
		const g = match.groups;
		// Belt and braces: if groups are ever missing (regex transform drift),
		// emit the raw match text instead of throwing mid-render.
		if (!g) {
			nodes.push(match[0]);
			last = re.lastIndex;
			continue;
		}
		if (g.code !== undefined) {
			nodes.push(<code key={key}>{g.codeText}</code>);
		} else if (g.link !== undefined) {
			if (allowLinks) {
				nodes.push(
					<a key={key} href={g.href} target='_blank' rel='noopener noreferrer'>
						{formatInline(g.linkText, `${key}-t`, false)}
					</a>
				);
			} else {
				nodes.push(g.link);
			}
		} else if (g.boldItalic !== undefined) {
			nodes.push(<strong key={key}><em>{formatInline(g.boldItalicText, `${key}-bi`, allowLinks)}</em></strong>);
		} else if (g.bold !== undefined) {
			nodes.push(<strong key={key}>{formatInline(g.boldText, `${key}-b`, allowLinks)}</strong>);
		} else {
			nodes.push(<em key={key}>{formatInline(g.italicText, `${key}-i`, allowLinks)}</em>);
		}
		last = re.lastIndex;
	}
	if (last < line.length) {
		nodes.push(line.slice(last));
	}
	return nodes;
}

// Renders agent message text to an array of React nodes. Non-strings pass
// through untouched.
export function renderAgentMarkdown(text) {
	if (typeof text !== 'string' || text === '') {
		return text;
	}
	const out = [];
	const lines = text.split('\n');
	lines.forEach((line, li) => {
		if (li > 0) {
			out.push('\n');
		}
		if (HEADING_RE.test(line)) {
			out.push(<strong key={`h-${li}`}>{formatInline(line.replace(HEADING_RE, ''), `h-${li}`)}</strong>);
		} else {
			out.push(...formatInline(line, `l-${li}`));
		}
	});
	return out;
}

export default renderAgentMarkdown;
